import { useState, useEffect, useMemo } from "react";
import pistoresiLogo from "@/assets/pistoresi-logo.png.asset.json";
import CompanyDetailsModal from "@/components/company/CompanyDetailsModal";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  MapPin, Clock, Phone, Mail, CheckCircle, Truck, Calendar, Fuel, Shield,
  CircleDollarSign, ArrowRight, ChevronRight, ShieldCheck, ShieldAlert, Route, Package,
  FileText, MoreHorizontal, PlusCircle, ArrowLeft, ChevronDown, X, MessageSquare,
} from "lucide-react";
import { useChatContext } from "@/contexts/ChatContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSpotRate } from "@/hooks/useSpotRate";
import RouteMapModal from "./RouteMapModal";
import NegotiateRateModal from "./NegotiateRateModal";
import BookLoadDialog from "./BookLoadDialog";
import { LoadBriefPlayer } from "@/components/load-brief/LoadBriefPlayer";

const formatDriveTime = (miles: number): { solo: string; team: string } => {
  if (miles <= 700) {
    const totalMin = Math.round((miles / 47) * 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const display = h > 0 ? `${h}h ${m}m` : `${m}m`;
    return { solo: display, team: display };
  }
  const soloDays = Math.round((miles / 700) * 10) / 10;
  const teamDays = Math.round((miles / (45.5 * 24)) * 10) / 10;
  return {
    solo: `~${soloDays.toFixed(1)} days`,
    team: `~${teamDays.toFixed(1)} days`,
  };
};

interface LoadRow {
  id: number;
  loadId?: string;
  ageMinutes: number;
  pickupDate: string;
  dropDate: string;
  originCity: string;
  originState: string;
  destCity: string;
  destState: string;
  pickups: number;
  drops: number;
  type: string;
  rate: number;
  weight: number;
  miles: number;
  isEstimatedMiles?: boolean;
  equipment: string;
  ratePerMile: number;
  company: string;
  verified?: boolean;
  createdBy?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactPhoneSecondary?: string | null;
  contactEmail?: string | null;
  lengthFt?: number | null;
  commodity?: string | null;
  paymentTerms?: string | null;
  specialInstructions?: string | null;
  referenceNumber?: string | null;
  loadNumber?: string | null;
  pieces?: number | null;
  fuelSurchargeRate?: number | null;
  accessorialCharges?: number | null;
  originLat?: number | null;
  originLon?: number | null;
  destLat?: number | null;
  destLon?: number | null;
  dbEquipmentType?: string | null;
  companyId?: string | null;
  briefAudioPath?: string | null;
  briefDurationSeconds?: number | null;
  companies?: { name?: string | null; documents_verified_at?: string | null } | null;
}

interface StopRecord {
  id: string;
  stop_number: number;
  stop_type: string;
  city: string;
  state: string;
  scheduled_date: string | null;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  name: string;
  instructions: string | null;
}

function formatAge(mins: number): string {
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m > 0) return `${h}h ${m}m ago`;
  return `${h}h ago`;
}

function getInitials(name: string): string {
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

// Spot rate is now computed by the broker-comp engine (useSpotRate hook)

function formatStopTime(start: string | null, end: string | null): string | null {
  if (!start) return null;
  const fmt = (t: string) => {
    const [hours, minutes] = t.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  return end ? `${fmt(start)} - ${fmt(end)}` : fmt(start);
}

function formatStopDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return dateStr; }
}

export function LoadDetailsPanel({ load }: { load: LoadRow }) {
  const { openChatWithUser } = useChatContext();
  const { profile, user } = useAuth();
  const isBroker = (profile?.user_type === 'broker' || profile?.user_type === 'shipper') && !profile?.is_demo_user;

  // Live broker-comp spot rate
  const { data: spotRateResult } = useSpotRate({
    originLat: load.originLat,
    originLon: load.originLon,
    destLat: load.destLat,
    destLon: load.destLon,
    laneMiles: load.miles,
    equipmentType: load.dbEquipmentType,
  });
  const spotRate = spotRateResult?.suggestedRate ?? null;

  const hasRate = load.rate != null && load.rate > 0;
  const postedRpm = hasRate && load.miles > 0 ? Math.round((load.rate / load.miles) * 100) / 100 : 0;
  const marketRpm = spotRateResult?.marketRpm ?? null;
  const rateDiff = hasRate && spotRate != null ? load.rate - spotRate : null;

  const isMultiStop = load.pickups > 1 || load.drops > 1;
  const stopsDisplay = isMultiStop
    ? `${load.pickups}p${load.drops}d (Multi-Stop)`
    : `${load.pickups + load.drops} (Direct)`;

  const referenceDisplay = load.referenceNumber || load.loadNumber || '—';

  const [routeOpen, setRouteOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [negotiateOpen, setNegotiateOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [stops, setStops] = useState<StopRecord[]>([]);
  const [stopsLoading, setStopsLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<{
    mc_number?: string | null; dot_number?: string | null;
    city?: string | null; state?: string | null;
    verification_status?: string | null; credit_score?: number | null; documents_verified_at?: string | null;
  } | null>(null);

  // Reset stops and company details when selected load changes
  useEffect(() => {
    setStops([]);
    setStopsLoading(false);
    setCompanyDetails(null);
    if (load.companyId) {
      supabase.from('companies')
        .select('mc_number, dot_number, city, state, verification_status, credit_score, documents_verified_at')
        .eq('id', load.companyId)
        .maybeSingle()
        .then(({ data }) => { if (data) setCompanyDetails(data); });
    }
  }, [load.loadId, load.companyId]);

  const handleViewRoute = async () => {
    setRouteOpen(true);
    if (load.loadId && stops.length === 0) {
      setStopsLoading(true);
      const { data } = await supabase
        .from('load_stops')
        .select('id, stop_number, stop_type, city, state, scheduled_date, scheduled_time_start, scheduled_time_end, name, instructions')
        .eq('load_id', load.loadId)
        .order('stop_number', { ascending: true });
      if (data && data.length > 0) {
        setStops(data as StopRecord[]);
      }
      setStopsLoading(false);
    }
  };

  // Build route list: use real stops if available, else fallback to origin/dest
  const routeStops = stops.length > 0 ? stops : [
    { id: '1', stop_number: 1, stop_type: 'pickup', city: load.originCity, state: load.originState, scheduled_date: null, scheduled_time_start: null, scheduled_time_end: null, name: '', instructions: null },
    { id: '2', stop_number: 2, stop_type: 'delivery', city: load.destCity, state: load.destState, scheduled_date: null, scheduled_time_start: null, scheduled_time_end: null, name: '', instructions: null },
  ];

  const handleMessage = () => {
    if (load.createdBy) {
      openChatWithUser(load.createdBy, load.contactName || load.company);
    }
  };
  const typeLabel = load.type === "FTL" ? "Full Truckload (FTL)" : load.type === "LTL" ? "Less Than Load (LTL)" : load.type || "FTL";

  // Parse Pick Hrs / Drop Hrs from special_instructions
  const pickHrsMatch = load.specialInstructions?.match(/Pick Hrs:\s*([^|]*)/i);
  const dropHrsMatch = load.specialInstructions?.match(/Drop Hrs:\s*([^|]*)/i);
  const pickHrs = pickHrsMatch?.[1]?.trim() || null;
  const dropHrs = dropHrsMatch?.[1]?.trim() || null;

  // Filter out Pick Hrs / Drop Hrs from special_instructions for clean comment display
  const cleanComments = load.specialInstructions
    ? load.specialInstructions.replace(/Pick Hrs:\s*[^|]*/gi, '').replace(/Drop Hrs:\s*[^|]*/gi, '').replace(/\|/g, '').trim()
    : null;

  return (
    <div className="border-t border-border bg-muted/30 px-4 py-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0 mb-4">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <div className="w-2 h-6 bg-brand-teal rounded-sm" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Load Details</span>
          <div className="flex items-center gap-1">
            <span className="text-xl font-extrabold text-foreground">{load.originCity}, {load.originState}</span>
            <ArrowRight className="h-4 w-4 text-brand-teal" />
            <ArrowRight className="h-4 w-4 -ml-2 text-brand-teal" />
            <span className="text-xl font-extrabold text-foreground">{load.destCity}, {load.destState}</span>
          </div>
          <Badge variant="secondary" className="text-[10px] font-semibold px-2 py-0.5">{load.miles.toLocaleString()} mi</Badge>
          {isMultiStop && (
            <Badge variant="outline" className="text-[10px] font-semibold px-2 py-0.5">{load.pickups}P · {load.drops}D</Badge>
          )}
        </div>
        {(() => {
          const verifiedAt = load.companies?.documents_verified_at ?? companyDetails?.documents_verified_at ?? null;
          const isBroker = profile?.user_type === "freight_broker";
          return (
            verifiedAt ? (
              <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-success-subtle text-success border border-success/30">
                <ShieldCheck className="h-3 w-3" /> Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground border border-border">
                <ShieldAlert className="h-3 w-3" /> Not Verified
              </span>
            )
          );
        })()}
      </div>

      {/* Optional Load Brief — only renders when the poster attached one */}
      {load.briefAudioPath && (
        <div className="mb-3">
          <LoadBriefPlayer
            path={load.briefAudioPath}
            durationSec={load.briefDurationSeconds ?? null}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="flex flex-col gap-3">
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
              <div className="flex items-center gap-1.5">
                <Route className="h-3.5 w-3.5 text-accent" />
                <span className="text-xs font-semibold text-foreground">Trip Overview</span>
              </div>
              <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={handleViewRoute}>VIEW ROUTE</Button>
            </CardHeader>
            <CardContent className="p-3 pt-3">
              <div className="flex items-start gap-2">
                <div className="flex flex-col items-center mt-0.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-accent" />
                  <div className="w-1 h-10 bg-accent/30 rounded-full" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">{load.originCity}, {load.originState} <span className="text-muted-foreground font-normal">(Origin)</span></p>
                  <p className="text-[10px] text-muted-foreground">{load.pickupDate}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex flex-col items-center mt-0.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-accent" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">{load.destCity}, {load.destState} <span className="text-muted-foreground font-normal">(Destination)</span></p>
                  <p className="text-[10px] text-muted-foreground">{load.dropDate}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-border">
                <ArrowLeft className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[11px] font-medium text-foreground">{load.miles.toLocaleString()} mi · Solo: {formatDriveTime(load.miles).solo} · Team: {formatDriveTime(load.miles).team}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
              <div className="flex items-center gap-1.5">
                <Truck className="h-3.5 w-3.5 text-accent" />
                <span className="text-xs font-semibold text-foreground">Equipment</span>
              </div>
              <Badge variant="outline" className="text-[10px] font-bold px-1.5 py-0">{typeLabel}</Badge>
            </CardHeader>
            <CardContent className="p-3 pt-2">
              <div className="space-y-1.5">
                {[
                  { label: "Trailer Type", value: load.equipment, icon: <ChevronDown className="h-3 w-3 text-muted-foreground" /> },
                  { label: "Length", value: load.lengthFt ? `${load.lengthFt} ft` : '—', icon: <X className="h-3 w-3 text-muted-foreground" /> },
                  { label: "Weight", value: `${load.weight.toLocaleString()} lbs`, icon: <ChevronDown className="h-3 w-3 text-muted-foreground" /> },
                  ...(load.pieces ? [{ label: "Pieces", value: `${load.pieces}`, icon: <ChevronDown className="h-3 w-3 text-muted-foreground" /> }] : []),
                  { label: "Commodity", value: load.commodity || '—', icon: <X className="h-3 w-3 text-muted-foreground" /> },
                  { label: "Stops", value: stopsDisplay, icon: <ChevronDown className="h-3 w-3 text-muted-foreground" /> },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">{row.label}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-medium text-foreground">{row.value}</span>
                      {row.icon}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardContent className="p-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Reference ID</span>
                <span className="text-[11px] font-medium text-foreground">{referenceDisplay}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Posted</span>
                <span className="text-[11px] font-medium text-foreground">{formatAge(load.ageMinutes)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Comments card - only shown if comments exist */}
          {cleanComments && (
            <Card className="shadow-none">
              <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
                <MessageSquare className="h-3.5 w-3.5 text-accent" />
                <span className="text-xs font-semibold text-foreground">Comments</span>
              </CardHeader>
              <CardContent className="p-3 pt-2">
                <p className="text-[11px] text-foreground leading-relaxed">{cleanComments}</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
              <CircleDollarSign className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-semibold text-foreground">Rate &amp; Earnings</span>
            </CardHeader>
            <CardContent className="p-3 pt-2">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Offered Rate</p>
                  <p className="text-2xl font-bold text-foreground font-mono">{hasRate ? `$${load.rate.toLocaleString()}` : 'Call'}</p>
                  {hasRate && <p className="text-[10px] text-muted-foreground">${load.ratePerMile.toFixed(2)}/mi</p>}
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Trip Distance</p>
                  <p className="text-lg font-bold text-foreground font-mono">{load.miles.toLocaleString()} mi</p>
                  <div className="flex gap-0.5 h-4 items-end mt-1">
                    {[65, 80, 55, 90, 70, 85, 45, 75].map((h, i) => (
                      <div key={i} className="flex-1 rounded-sm bg-brand-teal" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-3 pt-2 border-t border-border">
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Rate Breakdown</p>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">Spot Rate</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1 flex items-center">
                      <div className="w-full border-t-2 border-dashed border-destructive/60" />
                      <div className="w-2 h-2 rounded-full bg-destructive -ml-1 shrink-0" />
                    </div>
                    <span className="text-[11px] font-mono font-medium text-foreground">{spotRate != null ? `$${spotRate.toLocaleString()}` : '—'} <span className="text-muted-foreground ml-0.5 text-[9px]">{spotRate != null ? `${spotRateResult?.compCount || 0} comps` : 'insufficient data'}</span></span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">Rate vs. Market</span>
                  <span className={`text-[11px] font-mono font-semibold ${rateDiff != null ? (rateDiff > 0 ? 'text-success' : rateDiff < 0 ? 'text-destructive' : 'text-muted-foreground') : 'text-muted-foreground'}`}>
                    {rateDiff != null
                      ? rateDiff > 0
                        ? `+$${rateDiff.toLocaleString()} above market`
                        : rateDiff < 0
                          ? `-$${Math.abs(rateDiff).toLocaleString()} below market`
                          : 'At market'
                      : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">RPM</span>
                  <span className="text-[11px] font-mono font-medium text-foreground">
                    {hasRate ? `$${postedRpm.toFixed(2)}/mi` : '—'}
                    {hasRate && marketRpm != null && <span className="text-muted-foreground ml-1 text-[9px]">vs ${marketRpm.toFixed(2)}/mi market</span>}
                  </span>
                </div>
                {load.fuelSurchargeRate != null && load.fuelSurchargeRate > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Fuel Surcharge</span>
                    <span className="text-[11px] font-mono font-medium text-foreground">{load.fuelSurchargeRate}%</span>
                  </div>
                )}
                {load.accessorialCharges != null && load.accessorialCharges > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Accessorial</span>
                    <span className="text-[11px] font-mono font-medium text-foreground">${load.accessorialCharges.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <TooltipProvider>
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex-1">
                        <Button variant="outline" size="sm" className="w-full h-10 text-xs font-bold border-brand-teal text-brand-teal-deep hover:bg-brand-teal-subtle" disabled={isBroker} onClick={() => setNegotiateOpen(true)}>NEGOTIATE RATE</Button>
                      </span>
                    </TooltipTrigger>
                    {isBroker && <TooltipContent>Carrier accounts only</TooltipContent>}
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex-1">
                        {load.rate && load.rate > 0 ? (
                          <Button size="sm" className="w-full h-10 text-xs font-bold bg-brand-teal text-white hover:bg-brand-teal/90" disabled={isBroker} onClick={() => setBookOpen(true)}>BOOK LOAD</Button>
                        ) : (
                          <Button size="sm" variant="outline" className="w-full h-10 text-xs font-bold" disabled={isBroker} onClick={() => setNegotiateOpen(true)}>SUBMIT RATE</Button>
                        )}
                      </span>
                    </TooltipTrigger>
                    {isBroker
                      ? <TooltipContent>Carrier accounts only</TooltipContent>
                      : (!load.rate || load.rate <= 0) && <TooltipContent>No posted rate — submit your own rate</TooltipContent>}
                  </Tooltip>
                </div>
              </TooltipProvider>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-accent" />
                <span className="text-xs font-semibold text-foreground">Deadlines</span>
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" /></Button>
            </CardHeader>
            <CardContent className="p-3 pt-2 space-y-1.5">
              <div>
                <div className="flex items-center gap-2">
                  <PlusCircle className="h-3.5 w-3.5 text-accent" />
                  <span className="text-[10px] text-muted-foreground">Pickup</span>
                  <span className="text-[11px] font-bold text-foreground ml-auto">{load.pickupDate}</span>
                </div>
                {pickHrs && (
                  <div className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded w-fit mt-1 ml-5">
                    {pickHrs}
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-accent" />
                  <span className="text-[10px] text-muted-foreground">Delivery</span>
                  <span className="text-[11px] font-bold text-foreground ml-auto">{load.dropDate}</span>
                </div>
                {dropHrs && (
                  <div className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded w-fit mt-1 ml-5">
                    {dropHrs}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardContent className="p-0 divide-y divide-border">
              <button
                type="button"
                onClick={() => load.companyId && setCompanyModalOpen(true)}
                disabled={!load.companyId}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/40 disabled:hover:bg-transparent disabled:cursor-default transition-colors"
                title={load.companyId ? "View broker company details" : undefined}
              >
                <div className="flex items-center gap-1.5">
                  <FileText className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">Payment Terms</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] font-medium text-foreground">{load.paymentTerms || '—'}</span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                </div>
              </button>
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <Fuel className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">LaneRunner Fuel Card</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] font-medium text-muted-foreground">Coming soon</span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-3">
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
              <div className="flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-accent" />
                <span className="text-xs font-semibold text-foreground">Company Info</span>
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" /></Button>
            </CardHeader>
            <CardContent className="p-3 pt-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-9 w-9 rounded-md bg-brand-teal-deep flex items-center justify-center shrink-0">
                  <span className="text-[11px] font-extrabold text-white">{getInitials(load.company)}</span>
                </div>
                <div>
                  {load.companyId ? (
                    <button
                      onClick={() => setCompanyModalOpen(true)}
                      className="text-xs font-bold text-primary hover:underline cursor-pointer text-left"
                    >
                      {load.company}
                    </button>
                  ) : (
                    <p className="text-xs font-bold text-foreground">{load.company}</p>
                  )}
                  {companyDetails?.documents_verified_at ? (
                    <Badge className="mt-0.5 text-[9px] px-1.5 py-0 bg-success-subtle text-success border-0 gap-0.5"><Shield className="h-2.5 w-2.5" /> Verified</Badge>
                  ) : (
                    <span className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground border border-border">
                      Unverified Broker
                    </span>
                  )}
                </div>
              </div>

              {companyDetails?.credit_score != null && (
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-muted-foreground">Credit Score</span>
                    <span className="text-[11px] font-semibold text-foreground">{companyDetails.credit_score}/100</span>
                  </div>
                  <Progress value={companyDetails.credit_score} className="h-1.5 [&>div]:bg-success" />
                </div>
              )}

              {load.paymentTerms ? (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] text-muted-foreground">Payments</span>
                  <span className="text-[11px] font-medium text-muted-foreground">{load.paymentTerms}</span>
                </div>
              ) : null}

              <div className="border-t border-border pt-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Contact</p>
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="h-6 w-6 border border-border">
                    <AvatarFallback className="text-[9px] font-bold bg-secondary text-secondary-foreground">{load.contactName && load.contactName !== load.company ? getInitials(load.contactName) : '—'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    {load.contactName && load.contactName !== load.company && (
                      <p className="text-[11px] font-semibold text-foreground">{load.contactName}</p>
                    )}
                    {load.contactPhone && (
                       <a href={`tel:${load.contactPhone}`} className="text-[19px] text-brand-teal hover:underline font-medium flex items-center gap-1">
                         <Phone className="h-2.5 w-2.5" />{formatPhoneNumber(load.contactPhone)}
                      </a>
                    )}
                    {load.contactPhoneSecondary && (
                      <p className="text-[10px] text-muted-foreground">{formatPhoneNumber(load.contactPhoneSecondary)} (alt)</p>
                    )}
                    {load.contactEmail && (
                      <a href={`mailto:${load.contactEmail}`} className="text-[10px] text-muted-foreground hover:underline flex items-center gap-1">
                        <Mail className="h-2.5 w-2.5" />{load.contactEmail}
                      </a>
                    )}
                  </div>
                  <X className="h-3.5 w-3.5 text-muted-foreground cursor-pointer hover:text-foreground" />
                </div>
                <TooltipProvider>
                  <div className="flex gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex-1">
                          <Button variant="outline" size="sm" className="w-full h-7 text-[10px] gap-1" onClick={handleMessage} disabled={isBroker || !load.createdBy}><Mail className="h-3 w-3" /> Message</Button>
                        </span>
                      </TooltipTrigger>
                      {isBroker && <TooltipContent>Carrier accounts only</TooltipContent>}
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex-1">
                          <Button
                            size="sm"
                            className="w-full h-7 text-[10px] gap-1 bg-brand-teal text-white hover:bg-brand-teal/90"
                            disabled={isBroker || !load.contactPhone}
                            onClick={() => { if (load.contactPhone) window.open(`tel:${load.contactPhone}`, '_self'); }}
                          ><Phone className="h-3 w-3" /> Call</Button>
                        </span>
                      </TooltipTrigger>
                      {isBroker ? <TooltipContent>Carrier accounts only</TooltipContent> : !load.contactPhone ? <TooltipContent>No phone number available</TooltipContent> : null}
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>

              <div className="border-t border-border mt-3 pt-2 space-y-1">
                {companyDetails?.city && companyDetails?.state && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">{companyDetails.city}, {companyDetails.state}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 flex-wrap">
                  {companyDetails?.mc_number && (
                    <span className="text-[10px] text-muted-foreground">MC #{companyDetails.mc_number}</span>
                  )}
                  {companyDetails?.mc_number && companyDetails?.dot_number && (
                    <span className="text-[10px] text-muted-foreground">·</span>
                  )}
                  {companyDetails?.dot_number && (
                    <>
                      <span className="text-[10px] text-muted-foreground">DOT #{companyDetails.dot_number}</span>
                    </>
                  )}
                  {companyDetails?.documents_verified_at && (
                    <>
                      <span className="text-[10px] text-muted-foreground">·</span>
                      <CheckCircle className="h-3 w-3 text-success" />
                      <span className="text-[10px] text-success font-medium">Verified</span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <img src={pistoresiLogo.url} alt="Pistoresi Logistics" className="h-6 object-contain" />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">Freight Agents Wanted</span>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Bring your customers. Grow with a partner.</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {[
                  "Keep up to 70% of gross margin",
                  "Operations, compliance & back-office support",
                  "Agent-friendly culture & real growth",
                  "Reefer, dry van, LTL, flatbed & specialized",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1 shrink-0" />
                    <span className="text-xs text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
              <Button
                className="w-full h-10"
                onClick={() => {
                  window.open("https://pistoresilogistics.com/home", "_blank", "noopener,noreferrer");
                }}
              >
                Apply Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* View Route Modal with Map */}
      <RouteMapModal
        open={routeOpen}
        onOpenChange={setRouteOpen}
        load={{
          origin_city: load.originCity,
          origin_state: load.originState,
          destination_city: load.destCity,
          destination_state: load.destState,
          origin_latitude: load.originLat,
          origin_longitude: load.originLon,
          destination_latitude: load.destLat,
          destination_longitude: load.destLon,
          total_miles: load.miles,
          pickup_date: load.pickupDate,
          delivery_date: load.dropDate,
          id: load.loadId || load.id,
        }}
        stops={routeStops}
        stopsLoading={stopsLoading}
      />

      {load.companyId && (
        <CompanyDetailsModal
          isOpen={companyModalOpen}
          onClose={() => setCompanyModalOpen(false)}
          companyId={load.companyId}
          companyName={load.company}
        />
      )}

      <NegotiateRateModal
        open={negotiateOpen}
        onOpenChange={setNegotiateOpen}
        load={{
          loadId: load.loadId,
          originCity: load.originCity,
          originState: load.originState,
          destCity: load.destCity,
          destState: load.destState,
          rate: load.rate,
          miles: load.miles,
          equipment: load.equipment,
          pickupDate: load.pickupDate,
        }}
      />

      <BookLoadDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        load={{
          loadId: load.loadId,
          originCity: load.originCity,
          originState: load.originState,
          destCity: load.destCity,
          destState: load.destState,
          rate: load.rate,
          miles: load.miles,
          equipment: load.equipment,
          pickupDate: load.pickupDate,
          dropDate: load.dropDate,
          weight: load.weight,
          company: load.company,
        }}
      />
    </div>
  );
}
